<template>
  <div class="svc-wrap" ref="wrap">
    <!-- Section header -->
    <div class="svc-header">
      <p class="svc-eyebrow">{{ $t('about.whatWeOffer') }}</p>
      <h2 class="svc-title">{{ $t('about.ourServices') }}</h2>
      <div class="svc-title-divider" aria-hidden="true"></div>
      <p class="svc-subtitle">{{ $t('about.servicesDesc') }}</p>
    </div>

    <v-row>
      <v-col
        v-for="(service, index) in services"
        :key="index"
        cols="12"
        sm="6"
        md="3"
      >
        <div
          class="svc-card"
          :class="{ 'is-visible': inView }"
          :style="{ transitionDelay: `${index * 0.12}s` }"
        >
          <!-- Top accent bar -->
          <div class="svc-card-bar" :style="{ background: service.gradient }" aria-hidden="true"></div>

          <div class="svc-card-inner">
            <!-- Icon badge -->
            <div
              class="svc-icon"
              :style="{ background: service.gradient, boxShadow: `0 6px 20px ${service.shadow}` }"
              aria-hidden="true"
            >
              <v-icon size="26" color="white">{{ service.icon }}</v-icon>
            </div>

            <!-- Title -->
            <h3 class="svc-card-title">{{ $t(service.title) }}</h3>

            <!-- Description -->
            <p class="svc-card-desc">{{ $t(service.description) }}</p>

            <!-- Feature chips -->
            <div class="svc-features">
              <span
                v-for="(feature, idx) in service.features"
                :key="idx"
                class="svc-chip"
                :style="{ color: service.chipColor, borderColor: service.chipBorder }"
              >{{ $t(feature) }}</span>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Wrap ──────────────────────────────────────────────────────────────
.svc-wrap {
  padding: 40px 0 60px;
}

// ── Header ────────────────────────────────────────────────────────────
.svc-header {
  text-align: center;
  margin-bottom: 56px;
}

.svc-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 12px;

  .theme--dark & { color: lighten($blue, 28%); }

  [dir='rtl'] & {
    letter-spacing: 0;
    text-transform: none;
  }
}

.svc-title {
  font-size: clamp(1.5rem, 3.5vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: #1a1a2e;
  margin: 0 0 16px;

  .theme--dark & { color: #eeeef5; }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.5;
  }
}

.svc-title-divider {
  width: 48px;
  height: 3px;
  border-radius: 2px;
  margin: 0 auto 20px;
  background: linear-gradient(90deg, $blue, $purple, $red);
}

.svc-subtitle {
  font-size: clamp(0.92rem, 1.8vw, 1.05rem);
  line-height: 1.75;
  color: #6b6b88;
  max-width: 620px;
  margin: 0 auto;

  .theme--dark & { color: #9090aa; }

  [dir='rtl'] & { line-height: 1.9; }
}

// ── Card ──────────────────────────────────────────────────────────────
.svc-card {
  height: 100%;
  border-radius: 20px;
  border: 1px solid rgba($blue, 0.1);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 20px rgba($blue, 0.04);
  overflow: hidden;
  position: relative;
  opacity: 0;
  transform: translateY(28px);
  transition:
    opacity 0.65s ease-out,
    transform 0.65s ease-out,
    box-shadow 0.28s ease,
    border-color 0.28s ease;

  .theme--dark & {
    background: rgba(18, 18, 32, 0.88);
    border-color: rgba(255, 255, 255, 0.07);
  }

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 44px rgba($blue, 0.13);
    border-color: rgba($blue, 0.18);

    .theme--dark & { box-shadow: 0 16px 44px rgba(0, 0, 0, 0.32); }

    .svc-icon {
      transform: scale(1.1) rotate(-5deg);
    }
  }
}

// ── Top accent bar ────────────────────────────────────────────────────
.svc-card-bar {
  height: 3px;
  width: 100%;
}

// ── Inner padding ─────────────────────────────────────────────────────
.svc-card-inner {
  padding: 28px 24px 32px;
}

// ── Icon badge ────────────────────────────────────────────────────────
.svc-icon {
  width: 48px;
  height: 48px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

// ── Card title ────────────────────────────────────────────────────────
.svc-card-title {
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.35;
  color: #1a1a2e;
  margin-bottom: 10px;

  .theme--dark & { color: #eeeef5; }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.6;
  }
}

// ── Card description ──────────────────────────────────────────────────
.svc-card-desc {
  font-size: 0.88rem;
  line-height: 1.7;
  color: #6b6b88;
  margin: 0 0 20px;

  .theme--dark & { color: #9090aa; }

  [dir='rtl'] & { line-height: 1.9; }
}

// ── Feature chips ─────────────────────────────────────────────────────
.svc-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.svc-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 5px 10px;
  border-radius: 100px;
  border: 1px solid;
  letter-spacing: 0.01em;

  [dir='rtl'] & { letter-spacing: 0; }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 600px) {
  .svc-wrap { padding: 24px 0 40px; }
  .svc-header { margin-bottom: 36px; }
  .svc-card:hover { transform: translateY(-3px); }
}
</style>

<script>
export default {
  data () {
    return {
      inView: false,
      services: [
        {
          icon: 'mdi-cellphone',
          title: 'about.serviceMobile',
          description: 'about.serviceMobileDesc',
          gradient: 'linear-gradient(135deg, #3949ab, #5c6bc0)',
          shadow: 'rgba(57, 73, 171, 0.32)',
          chipColor: '#3949ab',
          chipBorder: 'rgba(57, 73, 171, 0.3)',
          features: ['about.featureIOS', 'about.featureAndroid', 'about.featureCrossPlatform']
        },
        {
          icon: 'mdi-monitor',
          title: 'about.serviceDesktop',
          description: 'about.serviceDesktopDesc',
          gradient: 'linear-gradient(135deg, #7c3aed, #9d5bf7)',
          shadow: 'rgba(124, 58, 237, 0.30)',
          chipColor: '#7c3aed',
          chipBorder: 'rgba(124, 58, 237, 0.28)',
          features: ['about.featureWindows', 'about.featureMac', 'about.featureLinux']
        },
        {
          icon: 'mdi-view-dashboard',
          title: 'about.serviceDashboard',
          description: 'about.serviceDashboardDesc',
          gradient: 'linear-gradient(135deg, #7c3aed, #e53935)',
          shadow: 'rgba(124, 58, 237, 0.25)',
          chipColor: '#e53935',
          chipBorder: 'rgba(229, 57, 53, 0.28)',
          features: ['about.featureAdmin', 'about.featureAnalytics', 'about.featureReports']
        },
        {
          icon: 'mdi-web',
          title: 'about.serviceWebsite',
          description: 'about.serviceWebsiteDesc',
          gradient: 'linear-gradient(135deg, #e53935, #f06292)',
          shadow: 'rgba(229, 57, 53, 0.28)',
          chipColor: '#e53935',
          chipBorder: 'rgba(229, 57, 53, 0.25)',
          features: ['about.featureResponsive', 'about.featureSEO', 'about.featureModern']
        }
      ]
    }
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.inView = true
          observer.disconnect()
        }
      },
      { threshold: 0.15 }
    )
    observer.observe(this.$refs.wrap)
  }
}
</script>
